import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import SimpleSiteImageUpload from '../../components/SimpleSiteImageUpload'
import { getAll, putOne } from '../../services/common_requests'

const SOCIAL_PLATFORMS = [
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'twitter',
  'linkedin',
  'whatsapp',
] as const

function socialPlatformIcon(platform: string): string {
  const p = platform.toLowerCase()
  if (p === 'facebook') return 'bi-facebook'
  if (p === 'instagram') return 'bi-instagram'
  if (p === 'tiktok') return 'bi-tiktok'
  if (p === 'youtube') return 'bi-youtube'
  if (p === 'twitter') return 'bi-twitter-x'
  if (p === 'linkedin') return 'bi-linkedin'
  if (p === 'whatsapp') return 'bi-whatsapp'
  return 'bi-link-45deg'
}

const MONGO_ID_RE = /^[a-f0-9]{24}$/i

function extractStandardData(res: any): any {
  return res?.data ?? null
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: any } })?.response?.data
  const message = data?.message ?? data?.payload?.message
  if (Array.isArray(message) && message.length) {
    return message.filter(Boolean).join(', ')
  }
  if (typeof message === 'string' && message.trim()) {
    return message.trim()
  }
  return fallback
}

function imageUrlForDisplay(url: string | undefined): string {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return `${process.env.REACT_APP_API_URL_IMAGE || ''}${url}`
}

const HeaderSiteSettingsSection: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      logoId: '',
      logoPreview: '',
      contactEmail: '',
      contactPhone: '',
      contactAddress: '',
      socialLinks: [] as { platform: string; url: string; isActive: boolean }[],
    },
    onSubmit: async (values) => {
      setSaving(true)
      try {
        const body: Record<string, unknown> = {
          contactEmail: values.contactEmail || undefined,
          contactPhone: values.contactPhone || undefined,
          contactAddress: values.contactAddress || undefined,
          socialLinks: values.socialLinks.map((s) => ({
            platform: String(s.platform || '').trim().toLowerCase(),
            url: s.url?.trim() || undefined,
            isActive: s.isActive !== false,
          })),
        }
        if (MONGO_ID_RE.test(values.logoId)) {
          body.logo = values.logoId
        }
        await putOne(body, '/site-settings/header')
        toast.success(
          intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SUCCESS.SAVED' }),
          { position: 'top-right' },
        )
      } catch (e) {
        toast.error(
          apiErrorMessage(
            e,
            intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.ERROR.SAVE' }),
          ),
        )
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await getAll({}, '/site-settings/header')
        const d = extractStandardData(res)
        if (!d || cancelled) return
        const logo = d.logo
        const logoId =
          typeof logo === 'object' && logo?._id
            ? String(logo._id)
            : logo
              ? String(logo)
              : ''
        const logoUrl =
          typeof logo === 'object' && logo?.url ? String(logo.url) : ''
        formik.setValues({
          logoId,
          logoPreview: logoUrl,
          contactEmail: d.contactEmail || '',
          contactPhone: d.contactPhone || '',
          contactAddress: d.contactAddress || '',
          socialLinks: Array.isArray(d.socialLinks)
            ? d.socialLinks.map((s: any) => ({
                platform: s.platform,
                url: s.url || '',
                isActive: s.isActive !== false,
              }))
            : [],
        })
      } catch (e) {
        toast.error(intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.ERROR.LOAD' }))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  if (loading) {
    return (
      <div className='text-center py-15'>
        <Spinner animation='border' className='text-primary' />
      </div>
    )
  }

  return (
    <div className='card card-flush shadow-sm mb-2'>
      <div className='card-header border-0 pt-6 align-items-start'>
        <div className='card-title flex-column'>
          <span className='fw-bold fs-3 text-gray-900'>
            {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.TITLE' })}
          </span>
          <span className='text-gray-400 mt-1 fw-semibold fs-6'>
            {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.INTRO' })}
          </span>
        </div>
      </div>
      <div className='card-body pt-2 pb-6 px-6'>
        <form onSubmit={formik.handleSubmit}>
          <Row className='g-6'>
            <Col md={4}>
              <label className='form-label fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.LOGO' })}
              </label>
              {formik.values.logoPreview && (
                <div className='mb-4'>
                  <img
                    src={imageUrlForDisplay(formik.values.logoPreview)}
                    alt=''
                    className='img-fluid rounded border border-gray-200 shadow-sm'
                    style={{ maxHeight: 100 }}
                  />
                </div>
              )}
              <SimpleSiteImageUpload
                onImageAdded={({ _id, url }) => {
                  formik.setFieldValue('logoId', _id)
                  formik.setFieldValue('logoPreview', url)
                }}
              />
            </Col>
            <Col md={8}>
              <Form.Group className='mb-5'>
                <Form.Label className='fw-semibold text-gray-700'>
                  {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.EMAIL' })}
                </Form.Label>
                <Form.Control
                  name='contactEmail'
                  className='form-control-solid'
                  value={formik.values.contactEmail}
                  onChange={formik.handleChange}
                  type='email'
                />
              </Form.Group>
              <Form.Group className='mb-5'>
                <Form.Label className='fw-semibold text-gray-700'>
                  {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.PHONE' })}
                </Form.Label>
                <Form.Control
                  name='contactPhone'
                  className='form-control-solid'
                  value={formik.values.contactPhone}
                  onChange={formik.handleChange}
                />
              </Form.Group>
              <Form.Group className='mb-0'>
                <Form.Label className='fw-semibold text-gray-700'>
                  {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.ADDRESS' })}
                </Form.Label>
                <Form.Control
                  name='contactAddress'
                  className='form-control-solid'
                  value={formik.values.contactAddress}
                  onChange={formik.handleChange}
                  as='textarea'
                  rows={3}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Réseaux sociaux (déplacés depuis le footer : appartiennent au header / top-bar) */}
          <div className='mt-8 border-top pt-6'>
            <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4'>
              <div>
                <span className='fw-bold fs-5 text-gray-900 d-block'>
                  {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SOCIAL.TITLE' })}
                </span>
                <span className='text-gray-500 fs-7'>
                  {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SOCIAL.HINT' })}
                </span>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-light-primary rounded-pill'
                onClick={() =>
                  formik.setFieldValue('socialLinks', [
                    ...formik.values.socialLinks,
                    { platform: 'facebook', url: '', isActive: true },
                  ])
                }
              >
                <i className='bi bi-plus-lg me-1' />
                {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SOCIAL.ADD' })}
              </button>
            </div>
            {formik.values.socialLinks.length === 0 && (
              <p className='text-gray-500 fs-7 mb-0 py-4'>
                {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SOCIAL.EMPTY' })}
              </p>
            )}
            {formik.values.socialLinks.map((row, idx) => (
              <div
                key={`${row.platform}-${idx}`}
                className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3 gap-md-4 py-3 border-bottom border-gray-100'
              >
                <div className='d-flex align-items-center gap-3 flex-grow-1 flex-wrap'>
                  <div className='w-40px h-40px rounded-circle bg-light-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                    <i className={clsx('bi fs-4 text-primary', socialPlatformIcon(row.platform))} />
                  </div>
                  <Form.Select
                    className='form-select form-select-solid w-100 min-w-150px'
                    value={row.platform}
                    onChange={(e) =>
                      formik.setFieldValue(`socialLinks.${idx}.platform`, e.target.value)
                    }
                  >
                    {SOCIAL_PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control
                    className='form-control-solid flex-grow-1 min-w-0'
                    value={row.url}
                    onChange={(e) =>
                      formik.setFieldValue(`socialLinks.${idx}.url`, e.target.value)
                    }
                    placeholder='https://'
                  />
                </div>
                <div className='d-flex align-items-center justify-content-between gap-3 flex-shrink-0'>
                  <div className='form-check form-switch form-check-custom form-check-solid mb-0'>
                    <Form.Check
                      type='switch'
                      id={`header-social-active-${idx}`}
                      label={intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SOCIAL.ACTIVE' })}
                      checked={row.isActive}
                      onChange={(e) =>
                        formik.setFieldValue(`socialLinks.${idx}.isActive`, e.target.checked)
                      }
                    />
                  </div>
                  <button
                    type='button'
                    className='btn btn-icon btn-sm btn-light-danger rounded-circle flex-shrink-0'
                    onClick={() => {
                      const next = [...formik.values.socialLinks]
                      next.splice(idx, 1)
                      formik.setFieldValue('socialLinks', next)
                    }}
                  >
                    <i className='bi bi-trash' />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className='d-flex justify-content-end mt-8'>
            <Button
              type='submit'
              variant='primary'
              className='rounded-pill px-6 shadow-sm d-flex align-items-center gap-2'
              disabled={saving}
            >
              {saving && <Spinner animation='border' size='sm' />}
              <i className='bi bi-check-lg' />
              {intl.formatMessage({ id: 'HEADER_SITE_SETTINGS.SAVE' })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default HeaderSiteSettingsSection
